import { useState, useEffect, useCallback } from "react";
import { useChatHistory } from "../ChatHistoryProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faFilter, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { useAuthContext } from "../AuthProvider";
import { useAgent } from "../AgentsContext"; // Import useAgent to get the agent name
import settings from "../settings";
import { usePatientProfile } from "../PatientProfileProvider";

const { REACT_APP_BACKEND_URL } = settings;

type Patient = {
  patient_id: string;
  patient_name: string;
  has_profile: boolean;
};


type Coach = {
  coach_id: string;
  coach_name: string;
};

type Goal = {
  description: string;
  progress: string;
};

type PatientData = {
  patient_name: string;
  patient_id: string;
  goals: Goal[];
  summary: string;
  number_of_sessions: number;
  formattedGoals: string[];
};

type FetchDataResponse = {
  patients: Patient[];
  coaches: Coach[];
  dates: string[];
};

const MessageWidget = () => {
  const { setPatientData, setIsInfoBarOpen } = usePatientProfile();
  const [textareaValue, setTextareaValue] = useState<string>("");
  const { sendMessage, selectedChat } = useChatHistory();
  const disabled = selectedChat?.isWaiting || textareaValue.trim().length === 0;

  const [patients, setPatients] = useState<Patient[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [dates, setDates] = useState<string[]>([]);

  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [selectedCoach, setSelectedCoach] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false); // Loading state

  const { loginWithMicrosoft } = useAuthContext();
  const { selectedAgent, selectedAgentName } = useAgent(); // Get selected agent name

  const [showPatientPopup, setShowPatientPopup] = useState<boolean>(false);
  const [loadingPatientProfile, setLoadingPatientProfile] = useState(false);

  const fetchData = useCallback(
    async (
      patientId: string | null = null,
      coachId: string | null = null,
      callDate: string | null = null
    ) => {
      setLoading(true); // Start loading
      try {
        const token = localStorage.getItem("access_token");

        if (!token) {
          console.warn("No access token found, redirecting to login.");
          loginWithMicrosoft();
          return [];
        }

        const response = await fetch(`${REACT_APP_BACKEND_URL}/search_filters/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agent_id: selectedAgent,
            patient_id: patientId,
            coach_id: coachId,
            call_date: callDate,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            loginWithMicrosoft();
          }
          throw new Error("Failed to fetch search filters");
        }

        const data: FetchDataResponse = await response.json();
        setPatients(data.patients);
        setCoaches(data.coaches);
        setDates(data.dates)
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    },
    [loginWithMicrosoft, selectedAgent]
  );

  const handleSelectChange = async (type: string, value: string | null) => {
    if (type === "patient") {
      setSelectedPatient(value);
    } else if (type === "coach") {
      setSelectedCoach(value);
    } else if (type === "date") {
      setSelectedDate(value);
    }

    await fetchData(
      type === "patient" ? value : selectedPatient,
      type === "coach" ? value : selectedCoach,
      type === "date" ? value : selectedDate
    );
  };

  const handleSendMessage = async (content: string) => {
    if (disabled) return;

    sendMessage(content, selectedPatient, selectedCoach, selectedDate);
    setTextareaValue("");
  };

const fetchPatientProfile = async (patientId: string | null) => {
  if (!patientId) return; // Ensure patientId is valid
  setLoadingPatientProfile(true);

  try {
    const token = localStorage.getItem("access_token");
    if (!token) {
      loginWithMicrosoft();
      return;
    }

    const response = await fetch(`${REACT_APP_BACKEND_URL}/patients/profile/${patientId}`, {
      method: "GET",
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      if (response.status === 401) {
        loginWithMicrosoft();
        return;
      } else if (response.status === 404) {
        console.error("Patient profile not found");
        setPatientData(null);
        return;
      }
      throw new Error("Failed to fetch patient profile");
    }

    const data = await response.json();

    // Helper function to format the goals
    const formatGoals = (goals: any[]) =>
      goals
        .sort((a, b) => new Date(a.goal_date).getTime() - new Date(b.goal_date).getTime()) // Sort by goal_date
        .map((goal) => ({
          title: new Date(goal.goal_date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          cardTitle: goal.description,
          cardSubtitle: `Goal date: ${new Date(goal.goal_date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}`,
          items: goal.achievement.map((achievement: { achievement_date: string | number | Date; achievement_description: any; }) => ({
            title: new Date(achievement.achievement_date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
            cardTitle: achievement.achievement_description,
            cardSubtitle: `Achievement date: ${new Date(achievement.achievement_date).toLocaleDateString(
              "en-US",
              { year: "numeric", month: "long", day: "numeric" }
            )}`,
          })),
        }));
    
      const formattedGoals = formatGoals(data.goals);
    // Update state with the patient profile data, including formatted goals
    setPatientData({
      patient_name: data.patient_name,
      patient_id: data.patient_id,
      goals: data.goals,
      formattedGoals: formattedGoals, // Add the formatted goals here
      summary: data.historical_summary,
      number_of_sessions: data.number_of_sessions,
    });
    console.log(formattedGoals)
    setIsInfoBarOpen(true); // Open InfoBar when profile data is set
  } catch (error) {
    console.error("Failed to fetch patient profile", error);
    setPatientData(null); // Clear data on error
    setIsInfoBarOpen(false);
  } finally {
    setLoadingPatientProfile(false);
  }
};

  useEffect(() => {
    if (showFilters) {
      fetchData();
    }
  }, [showFilters, fetchData]);

  useEffect(() => {
    if (!showFilters) {
      setSelectedPatient(null);
      setSelectedCoach(null);
      setSelectedDate(null);
    }
  }, [showFilters]);

  return (
    <div className="w-full border-t md:border-t-1 dark:border-white/20 pt-2 dark:bg-gray-800 bg-gray-200">
      <form
        className="mx-2 flex flex-row gap-3 last:mb-2 md:mx-4 md:last:mb-6 lg:mx-auto lg:max-w-2xl xl:max-w-3xl"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="relative flex h-full flex-1 md:flex-col">
          {showFilters && selectedAgentName !== "HR Assistant" && ( // Disable filters if selected agent name is "HR Assistant"
            <div className="relative flex flex-row space-x-2 mb-2">
              <select
                value={selectedCoach || ""}
                className="dark:text-white p-2 pl-4 pr-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  handleSelectChange("coach", e.target.value || null)
                }
                disabled={loading}
              >
                <option value="">Select Coach</option>
                {coaches
                  .sort((a, b) => a.coach_name.localeCompare(b.coach_name)) // Alphabetical sorting by name
                  .map((coach) => (
                    <option key={coach.coach_id} value={coach.coach_id}>
                      {coach.coach_name}
                    </option>
                  ))}
              </select>

              <select
                value={selectedPatient || ""}
                className="dark:text-white p-2 pl-4 pr-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  const newPatientId = e.target.value || null;
                  handleSelectChange("patient", newPatientId);
                  fetchPatientProfile(newPatientId);
                }}
                disabled={loading}
              >
                <option value="">Select Patient</option>
                <optgroup label="Patients with Profiles">
                  {patients
                    .filter((patient) => patient.has_profile)
                    .sort((a, b) => a.patient_name.localeCompare(b.patient_name)) // Alphabetical sorting
                    .map((patient) => (
                      <option key={patient.patient_id} value={patient.patient_id}>
                        {patient.patient_name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Patients">
                  {patients
                    .filter((patient) => !patient.has_profile)
                    .sort((a, b) => a.patient_name.localeCompare(b.patient_name)) // Alphabetical sorting
                    .map((patient) => (
                      <option key={patient.patient_id} value={patient.patient_id}>
                        {patient.patient_name}
                      </option>
                    ))}
                </optgroup>
              </select>

              <select
                value={selectedDate || ""}
                className="dark:text-white p-2 pl-4 pr-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                    handleSelectChange("date", e.target.value || null)
                }
                disabled={loading}
              >
                <option value="">Select Date</option>
                {dates
                  .sort((a, b) => a.localeCompare(b)) // Numerical sorting in string format (YYYYMMDD)
                  .map((date) => {
                    const originalDate = date;
                    const formattedDate = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
                    return (
                      <option key={originalDate} value={originalDate}>
                        {formattedDate}
                      </option>
                    );
                  })}
              </select>
            </div>
          )}
          <div className="flex flex-row items-center">
            <button
              type="button"
              className="flex p-2.5 items-center gap-2 rounded-md bg-white dark:bg-transparent hover:bg-gray-500/10 transition-colors duration-200 dark:text-white cursor-pointer mr-2 flex-shrink-0 border dark:border-white/20 border-black/20 disabled:text-gray-500 dark:disabled:text-gray-500"
              onClick={() => setShowFilters(!showFilters)}
              disabled={selectedAgentName==="HR Assistant"? true: false}
            >
              <FontAwesomeIcon icon={faFilter} /> Filters
            </button>
            <textarea
              placeholder="Send Message..."
              className="textarea w-full relative dark:text-white dark:dark:bg-gray-700"
              value={textareaValue}
              onChange={(event) => setTextareaValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSendMessage(textareaValue);
                }
              }}
              rows={1}
            ></textarea>
            <button
              className="absolute p-1 rounded-md text-gray-500 bottom-1.5 md:bottom-2.5 hover:bg-gray-100 enabled:dark:hover:text-gray-400 dark:hover:bg-gray-900 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent right-1 md:right-2 disabled:opacity-40"
              onClick={() => handleSendMessage(textareaValue)}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="text-sm" />
            </button>
          </div>
        </div>
      </form>
      {showPatientPopup &&(<div></div>
    )}
    </div>
  );
};

export default MessageWidget;
